import { z } from 'zod'
import { firestoreTimestamp } from './validators'

// Collection name constant — use this instead of hardcoding 'users' strings
export const USERS_COLLECTION = 'users' as const

// Schema version — bump when schema changes, add migration function
export const CURRENT_VERSION = { major: 7, minor: 0 } as const

export const StripeSubscriptionStatusSchema = z.enum([
  'incomplete',
  'incomplete_expired',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
  'paused',
])

export const UserSubscriptionStatusSchema = z.union([
  StripeSubscriptionStatusSchema,
  z.literal('inactive'),
])

export const PERIOD_END_CANCELABLE_SUBSCRIPTION_STATUSES = [
  'active',
  'trialing',
  'past_due',
] as const satisfies readonly z.infer<typeof StripeSubscriptionStatusSchema>[]

export const IMMEDIATE_CANCELABLE_SUBSCRIPTION_STATUSES = [
  'incomplete',
  'paused',
  'unpaid',
] as const satisfies readonly z.infer<typeof StripeSubscriptionStatusSchema>[]

/**
 * Subscription sub-document stored at users/{uid}.subscription
 *
 * status values:
 *   'inactive'          — unknown Stripe status fallback or local deactivation
 *   'incomplete'         — initial payment needs action
 *   'incomplete_expired' — initial payment window expired (terminal)
 *   'trialing'           — in free trial period
 *   'active'             — paid and active
 *   'past_due'           — payment failed, subscription still alive
 *   'unpaid'             — retries exhausted; non-entitling
 *   'paused'             — trial ended without a payment method; resumable
 *   'canceled'           — ended (user-requested or Stripe-confirmed)
 *
 * cancellationReason values:
 *   'user_requested'              — user canceled via the app
 *   'stripe_subscription_deleted' — Stripe confirmed via webhook
 *   'payment_failed'              — invoice payment failed
 *   'account_deleted'             — user deleted their account
 */
export const UserSubscriptionSchema = z.object({
  status: UserSubscriptionStatusSchema,
  stripeSubscriptionId: z.string().optional(),
  priceId: z.string().optional(), // Stripe price ID for the active plan
  currentPeriodEnd: firestoreTimestamp.optional(), // End of billing period
  trial: z.string().optional(), // Mirrors status during trial, set alongside trialEnd
  trialEnd: firestoreTimestamp.optional(), // When trial ends
  cancellationReason: z.string().optional(), // See cancellationReason values above
  canceledAt: firestoreTimestamp.optional(), // User-requested cancellation
  stripeConfirmedCanceledAt: firestoreTimestamp.optional(), // Stripe webhook confirmation
  stripePromoCode: z.string().optional(), // Promo code applied to this subscription
  deletedAt: firestoreTimestamp.optional(), // Set when account is deleted
  trialExpiringNotifiedFor: firestoreTimestamp.optional(), // Trial end the trial_expiring email was sent for
})

export const UserOnboardingSummarySchema = z
  .object({
    status: z.enum(['in_progress', 'completed']),
    attemptNumber: z.number().int().positive(),
    completedAt: firestoreTimestamp.optional(),
  })
  .strict()

/**
 * Top-level user document stored at users/{uid}
 */
export const UserSchema = z.object({
  email: z.string().optional(),
  lastName: z.string().optional(), // Added in V2
  // Persisted before checkout so webhook reconciliation does not depend on
  // eventually-consistent Stripe customer search.
  stripeCustomerIds: z.array(z.string()).optional(),
  subscription: UserSubscriptionSchema.optional(),
  onboarding: UserOnboardingSummarySchema.optional(),

  // API access addon fields
  hasApiAccess: z.boolean().optional(), // Whether API access addon is active
  apiAccessSubscriptionId: z.string().optional(), // Stripe subscription ID for the addon
  apiAccessStatus: z.string().optional(), // Status of the API access addon
  apiAccessCurrentPeriodEnd: firestoreTimestamp.optional(), // Addon billing period end
  apiAccessCreatedAt: firestoreTimestamp.optional(), // When addon was created
  apiAccessCanceledAt: firestoreTimestamp.optional(), // When addon was canceled (Group B write site, PR 4)

  // Legacy field being removed — existed in users but should have been in userData
  syncPreferences: z.any().optional(),

  // Document version — added by converter on every write
  _version: z.object({ major: z.number(), minor: z.number() }).optional(),
})

// Derived TypeScript types
export type UserSubscription = z.infer<typeof UserSubscriptionSchema>
export type StripeSubscriptionStatus = z.infer<typeof StripeSubscriptionStatusSchema>
export type UserSubscriptionStatus = z.infer<typeof UserSubscriptionStatusSchema>
export type UserOnboardingSummary = z.infer<typeof UserOnboardingSummarySchema>
export type User = z.infer<typeof UserSchema>

/**
 * Wire shape of the subscription returned to the frontend by getUserSubscription().
 * Timestamp fields from UserSubscriptionSchema are emitted as ISO-8601 strings —
 * the frontend never observes the {seconds, nanoseconds} Firestore JSON shape.
 */
export const SubscriptionResponseSchema = z.object({
  status: UserSubscriptionSchema.shape.status, // mirrors UserSubscription.status enum
  priceId: z.string().optional(), // Stripe price ID for the active plan
  currentPeriodEnd: z.string().optional(), // ISO-8601 — end of billing period
  trial: z.string().optional(), // mirrors status during trial
  trialEnd: z.string().optional(), // ISO-8601 — when trial ends
  cancellationReason: z.string().optional(), // see UserSubscription.cancellationReason values
  canceledAt: z.string().optional(), // ISO-8601 — user-requested cancellation
  stripeConfirmedCanceledAt: z.string().optional(), // ISO-8601 — Stripe webhook confirmation
  stripePromoCode: z.string().optional(), // Promo code applied to subscription
  deletedAt: z.string().optional(), // ISO-8601 — set when account is deleted
})

export type SubscriptionResponse = z.infer<typeof SubscriptionResponseSchema>
